import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { IconCustomExport } from '@rossum/ui/icons/tabler';
import { Button, CircularProgress, SvgIcon } from '@rossum/ui/material';
import { useGuardedDownloadOriginalDocuments } from '../../document/hooks/useGuardedDownloadOriginalDocuments';
import { ExportMutationState } from '../annotation-actions/config';
import { ExportMenu } from '../export-menu/ExportMenu';
import { DownloadFormat } from '../export-menu/formats';
import { useExportAnnotationsCrossQueue } from '../hooks/useExportAnnotationsCrossQueue';
import {
  useInvalidateDashboardData,
  useSetDashboardData,
} from '../hooks/useFetchDashboardData';
import { NormalizedAnnotations } from './utils';

type Props = {
  annotations: NormalizedAnnotations;
  queueName: string;
  exportMutationState: ExportMutationState;
};

export const ExportCTA = ({
  annotations,
  queueName,
  exportMutationState,
}: Props) => {
  const intl = useIntl();
  const { mutate: exportAnnotations, isLoading } =
    useExportAnnotationsCrossQueue();
  const downloadOriginalDocuments = useGuardedDownloadOriginalDocuments();
  const setDashboardData = useSetDashboardData();
  const invalidateDashboardData = useInvalidateDashboardData();

  const annotationIds = annotations.map(({ id }) => id);

  const mutationContext = exportMutationState.visible
    ? exportMutationState.context
    : null;

  const onExportSuccess = useCallback(() => {
    if (mutationContext === 'confirmed') {
      // Confirmed → exporting leaves the confirmed tab, remove rows optimistically
      setDashboardData(response => {
        if (!response) return response;

        return {
          ...response,
          results: response.results.filter(
            ({ id }) => !annotationIds.includes(id)
          ),
        };
      });
    } else {
      // Re-export (failed_export → exporting) stays on the exports tab, just refetch
      invalidateDashboardData();
    }
  }, [
    annotationIds,
    mutationContext,
    invalidateDashboardData,
    setDashboardData,
  ]);

  const handleDownload = useCallback(
    (format: DownloadFormat) => {
      exportAnnotations({
        format,
        selectedAnnotationsIds: annotationIds,
        queueName,
      });
    },
    [annotationIds, exportAnnotations, queueName]
  );

  // No `format` → export only (no download). With `format` → combined export + download in one request.
  const handleExport = useCallback(
    (format?: DownloadFormat) => {
      const idsSet = new Set(annotationIds);

      // Optimistically patch status to 'exporting' for immediate visual feedback
      setDashboardData(response => {
        if (!response) return response;

        return {
          ...response,
          results: response.results.map(annotation =>
            idsSet.has(annotation.id)
              ? { ...annotation, status: 'exporting' as const }
              : annotation
          ),
        };
      });

      exportAnnotations(
        {
          format,
          selectedAnnotationsIds: annotationIds,
          toExport: true,
          queueName,
        },
        { onSuccess: onExportSuccess }
      );
    },
    [
      annotationIds,
      exportAnnotations,
      onExportSuccess,
      queueName,
      setDashboardData,
    ]
  );

  const triggerDisabled = isLoading || annotationIds.length === 0;

  return (
    <ExportMenu
      exportMutationState={exportMutationState}
      onDownload={handleDownload}
      onExport={exportMutationState.visible ? handleExport : undefined}
      onAsyncDownload={() =>
        downloadOriginalDocuments(
          annotations.map(({ document, attachmentStatus }) => ({
            url: document,
            attachmentStatus,
          }))
        )
      }
      selectedItemsCount={annotationIds.length}
      disabled={triggerDisabled}
    >
      {() => (
        <Button
          disabled={triggerDisabled}
          variant="outlined"
          color="secondary"
          startIcon={
            isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <SvgIcon fontSize="large">
                <IconCustomExport />
              </SvgIcon>
            )
          }
          sx={{ height: 1 }}
          data-cy="export-btn"
        >
          {intl.formatMessage({
            id: 'containers.annotationList.selectionPanel.buttons.export',
          })}
        </Button>
      )}
    </ExportMenu>
  );
};
